/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  AccountLevelProperties,
  APIAccountLevelResponseInterface,
  RequestAccountLevelInterface,
  SettingsInterface,
} from '../../interface/account-level.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import accountApi from '../../utils/api/account';
import feeConfiguration from '../../utils/api/fee-configuration';
import accountv2Api from '../../utils/api/v2/accounts';

import { setNotificationMessage } from './NotificationManagementSlice';

// a thunk to fetch account level
export const fetchAccountsLevel = createAsyncThunk<
  APIAccountLevelResponseInterface,
  RequestAccountLevelInterface
>(
  'account-management/fetchAllAccountsLevel',
  async (data: RequestAccountLevelInterface, { dispatch }) => {
    try {
      const response = await accountApi.fetchAccountsFee(removeEmptyKeys(data));
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

// a thunk to refresh account level
export const refreshAccountsLevel = createAsyncThunk<
  APIAccountLevelResponseInterface,
  RequestAccountLevelInterface
>(
  'account-management/refreshAllAccountsLevel',
  async (data: RequestAccountLevelInterface) => {
    const response = await accountApi.fetchAccountsFee(removeEmptyKeys(data));
    return response.data;
  },
);


export const fetchSettings = createAsyncThunk<SettingsInterface>(
  'payout/fetchAccountRevenueTotal',
  async (_: void, { dispatch }) => {
    try {
      const response = await feeConfiguration.fetchPlatformLevel();
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchAccountLevelFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('account-management/fetchAccountManagementFilters', async (_, { dispatch }) => {
  try {
    const response = await accountv2Api.fetchAccountsListFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: AccountLevelProperties = {
  accountLevel: [],
  settings: null,
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const accountLevelSlice = createSlice({
  name: 'account-management',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state - The state
     * @param {any} action - The action
     */
    updateAccountLevelFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The state
     */
    disableAccountLevelFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<AccountLevelProperties>) => {
    builder
      .addCase(fetchAccountsLevel.pending, (state: AccountLevelProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAccountsLevel.fulfilled,
        (
          state: AccountLevelProperties,
          action: PayloadAction<APIAccountLevelResponseInterface>,
        ) => {
          state.accountLevel = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
          state.loading = false;
        },
      )
      .addCase(fetchAccountsLevel.rejected, (state: AccountLevelProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder.addCase(refreshAccountsLevel.fulfilled, (state, action) => {
      state.accountLevel = [...action.payload.results];
      state.limit = action.payload.limit;
      state.page = action.payload.page;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
    });
    builder
      .addCase(fetchAccountLevelFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchAccountLevelFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchAccountLevelFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
    builder
      .addCase(fetchSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSettings.fulfilled, (state, action: PayloadAction<SettingsInterface>) => {
        state.loading = false;
        state.settings = action.payload;
      })

      .addCase(fetchSettings.rejected, (state) => {
        state.loading = false;
      });
  },
});

// Export the actions
export const { updateAccountLevelFilters, disableAccountLevelFilterApplied, setSortData } =
  accountLevelSlice.actions;

// Export the reducer
export default accountLevelSlice.reducer;
